/* eslint-disable @typescript-eslint/no-explicit-any */
import { Check } from "lucide-react";
import { useState } from "react";

import { CHART_VARIANTS, SWITCH_GROUP_LABEL, currentVariant, specFor, variantGroupOf, type ChartVariant, type SwitchGroup } from "./catalog";
import { VariantGlyph } from "./ChartGlyphs";
import type { SWidget } from "./model";

/* Bindings a switch had to drop, remembered for this session so switching
 * back (e.g. Column -> KPI -> Column) brings the category/legend back. */
const stash = new Map<string, { datasetId: string | null | undefined; dims: string[]; meas: string[]; seriesField: string | null }>();

/** Everything a switch changes, plus a plain-words note about any binding
 * the new type can't use (and that switching back / Undo restores it). */
export function switchToVariant(widget: SWidget, v: ChartVariant): { patch: Partial<SWidget>; note: string | null } {
  const to = specFor(v.type);
  const q = { ...(widget.query ?? {}) };
  let dims = [...(q.dimensions ?? [])];
  let meas = [...(q.measures ?? [])];
  const lost: string[] = [];
  const prev = stash.get(widget.id);
  const kept = prev && prev.datasetId === widget.datasetId ? prev : undefined;
  if (kept) {
    if (to?.wells.axis && !dims.length) dims = [...kept.dims];
    if (to?.wells.maxValues !== 1 && meas.length === 1 && kept.meas.length > 1 && kept.meas[0] === meas[0]) meas = [...kept.meas];
    if (to?.wells.legend && !q.seriesField && kept.seriesField) q.seriesField = kept.seriesField;
  }
  // Remember the fullest binding seen so far, before anything is dropped.
  stash.set(widget.id, {
    datasetId: widget.datasetId,
    dims: dims.length ? [...dims] : kept?.dims ?? [],
    meas: meas.length >= (kept?.meas.length ?? 0) ? [...meas] : kept!.meas,
    seriesField: q.seriesField ?? kept?.seriesField ?? null,
  });
  if (to?.wells.maxValues === 1 && meas.length > 1) {
    lost.push(`${meas.length - 1} extra value${meas.length > 2 ? "s" : ""}`);
    meas = meas.slice(0, 1);
  }
  if (!to?.wells.axis && dims.length) {
    lost.push(dims.length > 1 ? "the category fields" : "the category field");
    dims = [];
  } else if (to && !to.wells.legend && dims.length > 1 && v.group !== "category-chart") {
    lost.push(`${dims.length - 1} extra category field${dims.length > 2 ? "s" : ""}`);
    dims = dims.slice(0, 1);
  }
  let seriesField = q.seriesField ?? null;
  if (seriesField && !to?.wells.legend) {
    lost.push("the legend split");
    seriesField = null;
  }
  const display = { ...(widget.display ?? {}) };
  const chart: Record<string, any> = { ...(display.chart ?? {}) };
  for (const [k, val] of Object.entries(v.chart)) {
    if (val === undefined) delete chart[k];
    else chart[k] = val;
  }
  if (Object.keys(chart).length) display.chart = chart;
  else delete display.chart;
  const title = widget.title && widget.title === specFor(widget.type)?.label ? to?.label ?? widget.title : widget.title;
  return {
    patch: { type: v.type, title, query: { ...q, dimensions: dims, measures: meas, seriesField }, display },
    note: lost.length ? `${v.label} can't use ${lost.join(" and ")} — set aside for now. Switch back and they return (or press Ctrl+Z).` : null,
  };
}

export function ChartTypeGallery({ widget, onSwitch }: { widget: SWidget; onSwitch: (v: ChartVariant) => void }) {
  const current = currentVariant(widget.type, widget.display?.chart);
  const own = variantGroupOf(widget.type);
  const [hover, setHover] = useState<ChartVariant | null>(null);
  if (!own) return null;
  const order: SwitchGroup[] = [own, ...(["category-chart", "part-to-whole", "number"] as SwitchGroup[]).filter((g) => g !== own)];
  const shown = hover ?? current;
  const needsMore = (v: ChartVariant) => {
    const to = specFor(v.type);
    return !!to?.wells.axis && !(widget.query?.dimensions ?? []).length && !!(widget.query?.measures ?? []).length;
  };
  return (
    <div className="st-gallery" onMouseLeave={() => setHover(null)}>
      {order.map((g, gi) => (
        <div key={g} className="st-gallery__group">
          <div className="st-gallery__label">
            {SWITCH_GROUP_LABEL[g]}
            {gi === 0 ? <span className="ds-faint"> · same data</span> : null}
          </div>
          <div className="st-gallery__grid">
            {CHART_VARIANTS.filter((v) => v.group === g).map((v) => {
              const on = current?.id === v.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  className={`st-gtile${on ? " is-on" : ""}`}
                  aria-pressed={on}
                  onClick={() => !on && onSwitch(v)}
                  onMouseEnter={() => setHover(v)}
                  onFocus={() => setHover(v)}
                  title={`${v.label} — ${v.hint}${needsMore(v) ? " (add a category field after switching)" : ""}`}
                >
                  {on ? <Check size={11} className="st-gtile__check" /> : null}
                  <VariantGlyph id={v.id} size={40} />
                  <span>{v.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div className="st-gallery__hint" aria-live="polite">
        {shown ? (
          <>
            <strong>{shown.label}</strong> — {shown.hint}
            {shown !== current && variantGroupOf(shown.type) !== own ? <span className="ds-faint"> · click to switch, your fields are kept where they fit</span> : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
